import type { Address, PortalProfile } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Input } from '../../components/shadcn/index.js';
import { Button, Card, FormField, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { useCustomerAuthStore } from '../../lib/customerAuthStore.js';
import { formatDate } from '../../lib/intl.js';

function addrToForm(a: Address | null): Record<string, string> {
  return {
    line1: a?.line1 ?? '',
    line2: a?.line2 ?? '',
    city: a?.city ?? '',
    region: a?.region ?? '',
    postalCode: a?.postalCode ?? '',
    country: a?.country ?? '',
  };
}

/** Build an Address from form fields, or null when empty. */
function formToAddr(f: Record<string, string>): Address | null {
  const line1 = f.line1 ?? '';
  const city = f.city ?? '';
  const country = (f.country ?? '').toUpperCase();
  if (!line1 && !city && !country) return null;
  return {
    line1,
    ...(f.line2 ? { line2: f.line2 } : {}),
    city,
    ...(f.region ? { region: f.region } : {}),
    ...(f.postalCode ? { postalCode: f.postalCode } : {}),
    country,
  };
}

export function PortalProfilePage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [editing, setEditing] = useState(false);
  const [billing, setBilling] = useState<Record<string, string>>(addrToForm(null));
  const [saving, setSaving] = useState(false);

  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const hydrate = (p: PortalProfile): void => {
    setProfile(p);
    setBilling(addrToForm(p.billingAddress));
  };

  useEffect(() => {
    void customerApi().get<PortalProfile>('/me').then((r) => hydrate(r.data)).catch(() => undefined);
  }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const r = await customerApi().patch<PortalProfile>('/me', {
        billingAddress: formToAddr(billing),
      });
      hydrate(r.data);
      setEditing(false);
      toast.success(t('profile.saved'));
    } catch {
      toast.error(t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!curPw.trim() || !newPw.trim()) return;
    setPwSaving(true);
    try {
      await customerApi().post('/auth/change-password', { currentPassword: curPw, newPassword: newPw });
      toast.success(t('profile.password.changed'));
      useCustomerAuthStore.getState().clear();
      navigate('/portal/login');
    } catch {
      toast.error(t('profile.password.error'));
    } finally {
      setPwSaving(false);
    }
  };

  const addrFields = (state: Record<string, string>, set: (s: Record<string, string>) => void, prefix: string): JSX.Element => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {(['line1', 'line2', 'city', 'region', 'postalCode', 'country'] as const).map((k) => (
        <FormField key={k} id={`${prefix}-${k}`} label={t(`profile.addr.${k}`)}>
          <Input id={`${prefix}-${k}`} value={state[k] ?? ''} onChange={(e) => set({ ...state, [k]: e.target.value })} maxLength={k === 'country' ? 2 : 200} />
        </FormField>
      ))}
    </div>
  );

  if (!profile) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const row = (label: string, value: string): JSX.Element => (
    <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-fg-muted">{label}</span><span className="text-end">{value}</span></div>
  );
  const addrLines = (a: Address | null): string =>
    a ? [a.line1, a.line2, a.city, a.region, a.postalCode, a.country].filter(Boolean).join(', ') : '—';

  return (
    <div className="flex flex-col gap-5 pb-16 lg:pb-0">
      <header>
        <h1 className="text-2xl font-semibold">{t('profile.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('profile.subtitle')}</p>
      </header>

      <Card className="p-5">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('profile.contact')}</h2>
          {!editing && <Button variant="secondary" onClick={() => setEditing(true)}>{t('profile.edit')}</Button>}
        </div>

        {!editing ? (
          <div className="divide-y divide-border">
            {row(t('profile.code'), profile.code)}
            {row(t('profile.phone'), profile.phone ?? '—')}
            {row(t('profile.terms'), String(profile.paymentTermsDays))}
            {row(t('profile.memberSince'), formatDate(profile.createdAt, { locale: i18n.language }))}
            {row(t('profile.billing'), addrLines(profile.billingAddress))}
            {row(t('profile.shipping'), addrLines(profile.shippingAddress))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <FormField id="profile-phone" label={t('profile.phone')}>
              <Input id="profile-phone" value={profile.phone ?? '—'} readOnly disabled dir="ltr" className="cursor-not-allowed opacity-70" />
              <p className="mt-1 text-xs text-fg-muted">{t('profile.phoneLocked', { defaultValue: 'Your phone number is your login and cannot be changed.' })}</p>
            </FormField>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('profile.billing')}</div>
              {addrFields(billing, setBilling, 'billing')}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => void save()} loading={saving}>{t('profile.save')}</Button>
              <Button variant="ghost" onClick={() => { hydrate(profile); setEditing(false); }}>{t('profile.cancel')}</Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('profile.password.title')}</h2>
        <p className="mb-3 text-xs text-fg-muted">{t('profile.password.hint')}</p>
        <form className="flex max-w-sm flex-col gap-3" onSubmit={changePassword} noValidate>
          <FormField id="cur-pw" label={t('profile.password.current')} required>
            <Input id="cur-pw" type="password" autoComplete="current-password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required />
          </FormField>
          <FormField id="new-pw" label={t('profile.password.new')} required>
            <Input id="new-pw" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
          </FormField>
          <Button type="submit" loading={pwSaving}>{t('profile.password.submit')}</Button>
        </form>
      </Card>
    </div>
  );
}
